import React, { useContext as useContextCore } from 'react'
import type { FunctionComponent } from 'react'
import { useSearchParam } from 'react-use'
import isEmpty from 'lodash/isEmpty'
import { ApolloProvider } from '@apollo/client/react'
import { jsonTryParse, atob } from '@ztechapps/common.utils'
import { setupApolloClient } from '../../config/graphql'
import { InvoiceContainer } from './InvoiceContainer'
import { InvoiceLinkExpired } from './InvoiceLinkExpired'
import { InvoiceViewShell } from '../invoice-view/InvoiceViewShell'
import { useMounted } from '../../hooks/useMounted'

export type ProviderProps = {
  children: React.ReactNode
  // Add any other props that might be passed to the provider
  [key: string]: any
}

export type ContextType = Omit<ProviderProps, 'children'> & {
  filter: Record<string, any>
  accessToken: string
  urlSearchParams: {
    filter: Record<string, any>
    accessToken: string
    [key: string]: any
  }
  // Include any other context values that are provided
  [key: string]: any
}

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider: FunctionComponent<ProviderProps> = ({ children, ...props }) => {
  // Browser-only: it builds its own Apollo client, and the invoice wants no SEO.
  // Rendering nothing until mounted keeps the server HTML and the hydration render identical.
  const mounted = useMounted()
  const paramsStr = useSearchParam('params')
  if (!mounted) return null
  // `atob` throws on malformed base64 (length % 4 === 1); wrap it so a garbage URL
  // routes to <InvoiceLinkExpired /> instead of crashing the React tree.
  let decoded: any = null
  try {
    decoded = jsonTryParse(atob(decodeURIComponent(paramsStr || '') || ''))
  } catch {
    decoded = null
  }
  const params = decoded && typeof decoded === 'object' ? decoded : null

  const uri = `/graphql?tenantId=DFT`

  const isInvalid = isEmpty(paramsStr) || isEmpty(params) || isEmpty(params?.accessToken) || isEmpty(params?.filter)
  // The shell supplies the anonymous Apollo client (empty-string token, localStorage untouched) the invoice
  // layout's Header requires to mount.
  if (isInvalid) {
    return (
      <InvoiceViewShell>
        <InvoiceLinkExpired />
      </InvoiceViewShell>
    )
  }

  const { filter, accessToken } = params as { filter: Record<string, any>; accessToken: string }

  const client = setupApolloClient({ accessToken, uri })

  const value: ContextType = {
    ...props,
    filter,
    accessToken,
    urlSearchParams: params,
  }

  return (
    <ApolloProvider client={client}>
      <CoreProvider value={value}>
        <InvoiceContainer>{children}</InvoiceContainer>
      </CoreProvider>
    </ApolloProvider>
  )
}

export const useContext = (): ContextType => useContextCore(context)
